import {
  BATCH_EVENT_LEDGERS,
  useBatchEventsRefresh,
  useBatchStepLog,
  useEventHistory,
  type EventHistoryArgs,
} from '@ctcells/api'
import { STRIP_EVENTS_TAIL, dataSourceFor } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'
import { useStreamState, useStreamSubscribe } from '@ctcells/realtime'
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'

import { DataTable } from '../atoms/DataTable.tsx'
import { EmptyState } from '../atoms/EmptyState.tsx'
import { noticeToneOf } from '../atoms/noticeTone.ts'
import { Segmented } from '../atoms/Segmented.tsx'
import { StateNotice, type StateNoticeTone } from '../atoms/StateNotice.tsx'
import { historyForBatch } from '../screens/batches/batchDetailModel.ts'
import { DEMO_BATCH_EVENTS } from '../screens/demoData.ts'

import {
  STRIP_EVENTS_FILTERS,
  isEmphasisRow,
  newRowsAbove,
  stripColumns,
  type ModuleEventName,
  stripEventRows,
  stripRowKey,
  type StripEventRow,
  type StripEventsFilter,
} from './stripEventsModel.tsx'

export type { StripEventsFilter } from './stripEventsModel.tsx'

/**
 * 상시 작업 줄의 **이벤트 칸** — 작업 줄 3행 오른끝 「▸ 이벤트」를 펼치면 선다(2026-09-28).
 *
 * 🔴 **출처는 「이벤트」 원장 둘이다** — `GET /history?event_type=event&batch_id=<id>` 와
 *    `…event_type=batch_record…`를 한 목록으로 섞는다(`@ctcells/api` `useEventHistory`). 2026-10-08(v0.9.4) 서버가
 *    배치 기록(발송 · 전이 · 멈춤 사유 …)을 `event_log` 에서 `batch_record` 로 뗐다 — 옛 작업은 `event`, 새 작업은
 *    `batch_record` 에 있다. 하나만 읽으면 새 작업의 칸이 모듈 상태 전이만 남고 비어 보인다.
 *    🔴 **감사 원장도 섞는다**(팀장 결정 2026-10-11) — 사람이 누른 시작 · 일시정지 · 재개 · 중지는 감사에만 있다.
 *    행 모양은 한 벌(`eventColumns.tsx`)이다.
 *    ⚠ `/logs` 가 아니다. 실측(2026-09-28): `/logs` 는 앱 로그(「request handled」 · 경로 · 상태
 *      코드)라 배치 축이 없고, `/history` 는 서버가 `batch_id` 로 **세 원장 전부를** 거른다
 *      (`app/services/history.py` `list_`). 시작 시각(`from`)으로 거를 필요가 없다.
 *
 * 🔴 **스냅샷 1회 + 구독**(DP-01). 펼칠 때 한 번 받고, 그 뒤로는 **이 작업에 닿는 구독 프레임이
 *    올 때만** 다시 받는다(`@ctcells/api` `useBatchEventsRefresh`). 시계로 다시 묻는 길은 없다.
 *    ⚠ 원장에 **있는** 사건의 구독 프레임(`module_status` …)은 행으로 그리지 않는다 — 같은 사건이
 *      두 번, 다른 얼굴로 선다. 원장이 SSOT 이고 그 프레임은 「새 행이 생겼을 수 있다」는 신호다.
 *
 * 🔴 **스텝 줄은 예외다**(eric 2026-09-28 「상태가 아니라 스텝별 내용을 보여줄 수 없어?」).
 *    스텝(`batch_progress` 의 `step_complete` …)은 **원장에 없다** — 서버가 저장하지 않는다. 그래서
 *    겹칠 사건이 없고, 구독 보관소(`@ctcells/api` `useBatchStepLog`)가 받은 것을 «스텝» 종류로 섞는다.
 *    새로 고치면 사라진다. ⚠ 그 사실을 적던 안내 한 줄(`strip.eventsStepNote`)은 eric 2026-09-29
 *    「이런 텍스트 다 빼줘」로 걷었다 — 되살리지 마라.
 * 🔴 **갈래**(전체 · 스텝 · 상태 변경 · 기타) — 상태 변경은 1초마다 쌓여 스텝을 덮는다. 고른 값은
 *    작업 줄 컨테이너가 쥔다(칸이 접혔다 펴져도 유지 · URL 아님).
 *
 * 🔴 **권한** — `/history` 는 operator·admin 한정이다(`history.py PRIVILEGED_ROLES`). viewer 는
 *    403 이고, 그 사유는 **이 칸에만** 선다. 작업 줄·본문은 그대로 산다.
 * 🔴 **실시간 로그 모양**(eric 2026-10-06 「일반적으로 쓰는 방식으로 해줘」) — 쪽 넘김이 없다. 최근 `STRIP_EVENTS_TAIL` 행만
 *    새것이 위로 선다. 종전 쪽 나눔은 새 행이 생길 때마다 뒤 쪽이 밀렸고, 「전부」 쪽은 스텝 줄이 얹혀 행 수가 쪽마다 달랐다.
 *    - 원장은 최근 `STRIP_EVENTS_WINDOW` 행을 받는다(갈래가 같은 캐시를 쓴다). 스텝 줄(구독 보관소)과 시각으로 섞어 거른 뒤
 *      위에서 `STRIP_EVENTS_TAIL` 행을 자른다 — 섞인 최근 50행의 원장 몫은 받은 100행 안에 든다.
 *      「상태 변경」·「기타」는 서버에 가름 인자가 없어(`/history` 인자는 `event_type`·`batch_id`·`from`·`to`·`limit`·`offset`
 *      뿐 · 실측 2026-09-30) 그 100행 안에서 거른다.
 *    - **따라가기** — 맨 위를 보고 있으면 새 행이 바로 선다. 아래로 내려 읽는 중이면 보이는 행을 붙잡고
 *      「새 이벤트 n건」을 띄운다 — 누르면 맨 위(최신)로 돌아간다(`useFollowLatest`). 읽던 행이 밀려 내려가지 않는다.
 *    - 더 오래된 기록 · 내려받기는 작업 상세의 이벤트 탭이다 — 실행 중 내려받기는 «그때까지»의 중간본이라 이 칸에 두지 않는다.
 *    - 구독 신호는 같은 원장을 다시 받는다(같은 무효화)
 * 🔴 **stale** — 구독이 끊기면 「새 이벤트가 반영되지 않는다」를 말한다. 받아 둔 행은 **과거
 *    기록**이라 지우지 않는다(「지금 상태」가 아니다). 다시 붙으면 한 번 다시 받는다.
 */
export interface StripEventsProps {
  /** 모듈 카드에서는 갈래 없이 전체 목록을 표시한다. 통합 관제는 기본 선택기를 유지한다. */
  showFilters?: boolean

  /** 작업 줄이 지금 보이는 작업. 없으면 빈 상태다 */
  batchId: string | undefined
  /** 갈래. 넘기지 않으면 칸 안에서 쥔다(접으면 잊는다) */
  filter?: StripEventsFilter | undefined
  onFilterChange?: ((filter: StripEventsFilter) => void) | undefined
  /** 칸을 닫는다 — 넘기면 갈래 줄 오른쪽 끝에 × 가 선다(eric 2026-10-06 「오른쪽에.. 닫기 버튼」) */
  onClose?: (() => void) | undefined
}

/**
 * 원장에서 받는 최근 행 수 — 「상태 변경」·「기타」는 서버에 가름 인자가 없어 이 안에서 거른다(위 주석).
 * 서버 최대 500 아래. 보이는 행(`STRIP_EVENTS_TAIL`)보다 넉넉해야 섞인 최근 행의 원장 몫이 빠지지 않는다
 */
const STRIP_EVENTS_WINDOW = 100

/** 맨 위로 보는 여유(px) — 이만큼 안이면 «맨 위를 보고 있다»(따라간다) */
const FOLLOW_SLACK_PX = 4

export function StripEvents({
  batchId,
  filter,
  onFilterChange,
  onClose,
  showFilters = true,
}: StripEventsProps) {
  const stale = useStreamState('events').stale
  const [localFilter, setLocalFilter] = useState<StripEventsFilter>('all')
  const current = filter ?? localFilter
  const changeFilter = onFilterChange ?? setLocalFilter
  return (
    <div className="strip-events">
      {stale ? (
        <p className="strip-events__stale" role="status">
          <span aria-hidden="true">⚠</span> {t('strip.eventsStale')}
        </p>
      ) : null}
      {batchId === undefined ? (
        <>
          {onClose === undefined ? null : (
            <div className="strip-events__bar">
              <CloseButton onClose={onClose} />
            </div>
          )}
          <EmptyState label={t('strip.eventsNoBatch')} />
        </>
      ) : (
        // 작업이 바뀌면 칸이 새로 선다 — 남의 작업에서 붙잡은 행을 이어 보지 않는다
        <StripEventsBody
          key={batchId}
          batchId={batchId}
          filter={current}
          onClose={onClose}
          onFilterChange={changeFilter}
          showFilters={showFilters}
          stale={stale}
        />
      )}
    </div>
  )
}

function StripEventsBody({
  batchId,
  filter,
  onClose,
  onFilterChange,
  showFilters,
  stale,
}: {
  batchId: string
  filter: StripEventsFilter
  onClose: (() => void) | undefined
  onFilterChange: (filter: StripEventsFilter) => void
  showFilters: boolean
  stale: boolean
}) {
  return (
    <>
      {showFilters || onClose !== undefined ? (
        <div className="strip-events__bar">
          {showFilters ? (
            <Segmented
              label={t('strip.eventsFilter')}
              onChange={onFilterChange}
              options={STRIP_EVENTS_FILTERS.map((value) => ({
                value,
                label: t(`strip.eventsFilter.${value}`),
              }))}
              value={filter}
            />
          ) : null}
          {onClose === undefined ? null : <CloseButton onClose={onClose} />}
        </div>
      ) : null}
      {/* 갈래를 바꾸면 표가 새로 선다 — 붙잡은 행을 버리고 맨 위(최신)부터 본다 */}
      {dataSourceFor('logs') === 'live' ? (
        <StripEventsLive key={filter} batchId={batchId} filter={filter} stale={stale} />
      ) : (
        <StripEventsDemo key={filter} batchId={batchId} filter={filter} />
      )}
    </>
  )
}

/** 칸 닫기 × — 서랍 머리의 닫기(`PageHeader` `.closex`)와 같은 모양 · 같은 이름 */
function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      aria-label={t('action.close')}
      className="closex"
      onClick={onClose}
      title={t('action.close')}
      type="button"
    >
      <span aria-hidden="true">×</span>
    </button>
  )
}

function StripEventsDemo({ batchId, filter }: { batchId: string; filter: StripEventsFilter }) {
  const steps = useBatchStepLog(batchId)
  const rows = stripEventRows(historyForBatch(DEMO_BATCH_EVENTS, batchId), steps, filter)
  return <StripEventsTable rows={rows.slice(0, STRIP_EVENTS_TAIL)} />
}

function StripEventsLive({
  batchId,
  filter,
  stale,
}: {
  batchId: string
  filter: StripEventsFilter
  stale: boolean
}) {
  const args = useMemo<EventHistoryArgs & { batch_id: string }>(
    () => ({
      batch_id: batchId,
      // 🔴 감사까지 셋 — 사람이 누른 시작 · 중지는 감사 원장에만 있다(`BATCH_EVENT_LEDGERS` 머리말)
      ledgers: BATCH_EVENT_LEDGERS,
      limit: STRIP_EVENTS_WINDOW,
      // 「스텝」 갈래는 원장을 안 쓴다
      ...(filter === 'step' ? { skip: true } : {}),
    }),
    [batchId, filter],
  )
  const events = useEventHistory(args)
  useBatchEventsRefresh(useStreamSubscribe(), args, stale)
  const steps = useBatchStepLog(batchId)
  const items = events.data?.items
  const rows = useMemo(
    () => stripEventRows(items ?? [], steps, filter).slice(0, STRIP_EVENTS_TAIL),
    [filter, items, steps],
  )
  const failed = filter !== 'step' && events.isError

  return (
    <StripEventsTable
      loading={filter !== 'step' && events.isPending}
      rows={rows}
      {...(failed
        ? { notice: errorMessage(events.error), noticeTone: noticeToneOf(events.error) }
        : {})}
    />
  )
}

/** 가장 가까운 세로 스크롤 상자 — 작업 줄에서는 펼친 이벤트 칸(`.strip__events`)이다. 없으면(시험 · 다른 셸) 따라가기가 없다 */
function scrollParentOf(node: HTMLElement | null): HTMLElement | null {
  for (let el = node; el !== null; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY
    if (overflow === 'auto' || overflow === 'scroll') return el
  }
  return null
}

/**
 * **따라가기** — 맨 위를 보고 있으면 새 행을 그대로 보이고, 내려 읽는 중이면 그때 보이던 행을 붙잡는다.
 * 붙잡은 동안 새로 선 행 수(`fresh`)를 내고, `jumpToLatest` 가 맨 위로 올려 놓는다. `rootRef` 는 이 표를 감싼 상자다.
 */
function useFollowLatest(
  rows: readonly StripEventRow[],
  rootRef: RefObject<HTMLDivElement | null>,
) {
  const boxRef = useRef<HTMLElement | null>(null)
  const latest = useRef(rows)
  useEffect(() => {
    latest.current = rows
  }, [rows])
  const [held, setHeld] = useState<readonly StripEventRow[] | null>(null)

  useEffect(() => {
    const box = scrollParentOf(rootRef.current)
    boxRef.current = box
    if (box === null) return
    // 갈래 · 작업이 바뀌어 새로 섰으면 맨 위(최신)부터
    box.scrollTop = 0
    const onScroll = () => {
      const away = box.scrollTop > FOLLOW_SLACK_PX
      setHeld((now) => (away ? (now ?? latest.current) : null))
    }
    box.addEventListener('scroll', onScroll, { passive: true })
    return () => box.removeEventListener('scroll', onScroll)
  }, [rootRef])

  const jumpToLatest = () => {
    if (boxRef.current !== null) boxRef.current.scrollTop = 0
    setHeld(null)
  }
  return {
    shown: held ?? rows,
    fresh: held === null ? 0 : newRowsAbove(rows, held),
    jumpToLatest,
  }
}

export function StripEventsTable({
  rows,
  loading = false,
  showStepProgress = true,
  highlightLatest = false,
  moduleNames,
  notice,
  noticeTone,
}: {
  rows: readonly StripEventRow[]
  loading?: boolean
  /** 원심 모듈은 내용 뒤의 n/N 진행 수를 생략한다. */
  showStepProgress?: boolean
  /** 모듈 이벤트 카드에서 실제 최신 행만 현재 행으로 표시한다. */
  highlightLatest?: boolean
  moduleNames?: ReadonlyMap<string, ModuleEventName> | undefined
  notice?: string
  noticeTone?: StateNoticeTone
}) {
  const columns = useMemo(
    () => stripColumns(showStepProgress, moduleNames),
    [showStepProgress, moduleNames],
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const { shown, fresh, jumpToLatest } = useFollowLatest(rows, rootRef)
  const latestKey = rows[0] === undefined ? undefined : stripRowKey(rows[0])
  return (
    <div className="strip-events__list" ref={rootRef}>
      {fresh > 0 ? (
        <div className="strip-events__new">
          <button className="strip-events__newbtn" onClick={jumpToLatest} type="button">
            {t('strip.eventsNew', { count: fresh })}
          </button>
        </div>
      ) : null}
      {notice !== undefined && shown.length > 0 ? (
        <StateNotice reason={notice} tone={noticeTone} />
      ) : null}
      <DataTable
        caption={t('strip.eventsCaption')}
        columns={columns}
        current={
          highlightLatest && latestKey !== undefined
            ? (row) => stripRowKey(row) === latestKey
            : undefined
        }
        density="compact"
        emphasis={isEmphasisRow}
        emptyLabel={t('state.empty.events')}
        loading={loading}
        {...(notice === undefined || shown.length > 0 ? {} : { notice })}
        {...(noticeTone === undefined ? {} : { noticeTone })}
        rowKey={stripRowKey}
        rows={shown}
      />
    </div>
  )
}
